/**
 * CandidateCard (v2) — canonical shared card for the v2 Candidates card view.
 *
 * Full 10-field parity per design/v2-direction/index.html `.cand-card` spec
 * (lines 470–615, data schema lines 1297–1309, renderer lines 1584–1617).
 *
 * FIELD MAP (spec → real data source):
 *  1. Rank badge      — displayRank (from rankings.rank_position)
 *  2. Fit score       — fitScore (rankings.fit_score ONLY; "—" when unranked)
 *  3. Avatar          — CandidatePhoto (real photo blob by candidateId; initials fallback)
 *  4. Name            — candidate.name
 *  5. Status badge    — candidate.status (CandidateStatus enum)
 *  6. Key skills      — skillsTags (rankings.skills_tags, JD-anchored, evidence-gated)
 *  7. AI why blurb    — summary (rankings.summary, 3-line-clamped)
 *  8. Tenure chip     — tenureSignal (rankings.tenure_signal: strong/moderate/concern)
 *  9. Fit band chip   — fitOverall (rankings.fit_overall: strong/moderate/weak)
 * 10. View profile    — Link to /:tenantSlug/v2/candidates/:id[?jd=<id>]
 *
 * Honest-empty policy:
 *  - "Current role" (spec `c.role` field, `.cc-role`) has NO backend source;
 *    the Candidate and RankedCandidate APIs carry no title/employer field.
 *    Field OMITTED from the card — not fabricated.
 *  - "Last action / recency" (spec `c.act` field): spec card HTML does NOT
 *    render `act` in the .cand-card template (only in the table view). Field
 *    OMITTED from the card — not fabricated.
 *  - skillsTags: hidden (not rendered) when empty — no regex/lorem fallback.
 *  - tenureSignal / fitOverall: hidden when null — no invented placeholder.
 *
 * Density ceiling (sentinels grep source incl. comments):
 * No p-[56789], gap-[56789], space-y-[56789] anywhere in this file.
 * No px-[89], py-[89] anywhere in this file.
 *
 * Token discipline:
 * No raw hex literals. Surface/text/border semantics use the direct v2 token
 * utilities (bg-surface-card, text-text-secondary, border-border-default, …) and,
 * for inline style, the direct semantic var (var(--surface-subtle), color-mix(…)).
 * Only the fit-band / brand set (--strong, --moderate, --weak, --brand, --brand-soft,
 * --strong-soft, --moderate-soft, --weak-soft) are genuine HSL triples and safe
 * inside hsl(). The flat shadcn vars (--foreground, --muted-foreground, --card,
 * --muted, --border, --secondary, --secondary-foreground, --accent) re-point to
 * HEX under [data-theme="v2"], so wrapping them in hsl() is INVALID CSS (black/
 * invisible) — never do that. No hsl(var(--chart-N)) either (chart tokens are HEX).
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { CandidatePhoto } from "@/components/candidates/CandidatePhoto";
import { type FitOverall } from "@/lib/fitScore";
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import type { TenureSignal } from "@/hooks/queries/candidates/useRanking";
import { cn } from "@/lib/utils";

// ── Status labels (spec data: status:'Interviewing', st:'secondary', etc.) ──
const STATUS_LABELS: Record<CandidateStatus, string> = {
  active: "Active",
  phone_screen: "Phone screen",
  interview_scheduled: "Interview scheduled",
  interviewed: "Interviewed",
  offer: "Offer",
  hired: "Hired",
  rejected: "Rejected",
  opted_out: "Opted out",
};

/**
 * Status badge variant — spec uses `badge-secondary` for terminal-visible
 * milestones (Interviewing, Interviewed, Offer, Hired) and `badge-outline`
 * for all others. Matches spec's `c.st` field pattern.
 */
function statusVariant(status: CandidateStatus): "secondary" | "outline" {
  switch (status) {
    case "interview_scheduled":
    case "interviewed":
    case "offer":
    case "hired":
      return "secondary";
    default:
      return "outline";
  }
}

// ── Terminal (opted-out / rejected) statuses ─────────────────────────────────
const TERMINAL_STATUSES = new Set<CandidateStatus>(["opted_out", "rejected"]);

// ── Fit band = the fit CHIP's own source (categorical fit_overall) ───────────
// The score-number COLOR, header tint, and top-border band off the SAME source
// as the fit chip — `fit_overall` — so the number-color can never contradict
// the chip's category. (A prior bespoke numeric re-band, Strong 85+/Moderate
// 65–84/Weak <65, disagreed with the chip; banding off `fit_overall` removes
// the second, disagreeing threshold entirely.)
//
// Note the score NUMBER itself is now the raw `fit_score` (single source, matches
// the table) — no `fitScoreFromOverall` fabrication — so there is no invented
// number for the color to disagree with either.
//
// The band drives the green/amber/red fit tokens (--strong/--moderate/--weak),
// the exact palette the chip renders — do NOT recolor to blue.

// ── Props ─────────────────────────────────────────────────────────────────────

interface CandidateCardProps {
  // Field 1: rank badge
  /** 1-based rank position from rankings.rank_position. Null when not ranked. */
  displayRank: number | null;

  // Field 2: fit score
  /** 0–100 fit score from rankings.fit_score. Null for legacy/unranked rows. */
  fitScore: number | null;
  /** Categorical fit from rankings.fit_overall; drives the fit chip + score color. */
  fitOverall: FitOverall | null;

  // Field 3: avatar
  candidateId: string;
  /** Candidate name — drives initials + deterministic gradient fallback. */
  name: string;

  // Field 4 (name) — carried by `name` prop above.

  // Field 5: status badge
  status: CandidateStatus;

  // Field 6: key skills chips (rankings.skills_tags)
  /**
   * JD-anchored, evidence-gated skill tags from rankings.skills_tags.
   * Rendered up to 3. Hidden when empty — NO regex/lorem fallback.
   */
  skillsTags?: string[] | null;

  // Field 7: AI why blurb (rankings.summary)
  /** 3-line-clamped AI analysis sentence from rankings.summary. */
  summary?: string | null;

  // Field 8: tenure chip
  /** Tenure signal from rankings.tenure_signal. */
  tenureSignal?: TenureSignal | null;

  // Fields 9 (fit chip) + 10 (view profile link) are derived/internal.

  /** JD id — appended as ?jd= on the profile link for rankings context. */
  selectedJdId?: string | null;

  /**
   * Inline stage-advance control rendered in the card footer (V1 parity — the
   * card used to only offer "View profile"). Supplied by the page so the card
   * itself stays presentation-only (no mutation hook / no QueryClient dep in
   * unit tests). Omitted → footer shows the full-width "View profile" link.
   */
  statusControl?: ReactNode;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CandidateCard({
  displayRank,
  fitScore,
  fitOverall,
  candidateId,
  name,
  status,
  skillsTags,
  summary,
  tenureSignal,
  selectedJdId,
  statusControl,
}: CandidateCardProps): JSX.Element {
  const base = useTenantBase();

  const isTerminal = TERMINAL_STATUSES.has(status);

  // Fit score: terminal cards hide score (spec: `c.terminal?'':cc-fit`).
  // SINGLE SOURCE — the card's fit NUMBER reads ONLY `rankings.fit_score`, the
  // exact same source the v2 table column uses. No fabricated fallback: a prior
  // `fitScore ?? fitScoreFromOverall(fitOverall)` invented "55" for a moderate
  // row with a NULL stored score, so the card showed 55 while the table showed
  // "—" for the same candidate. Both surfaces now render the real score, or an
  // honest "—" when unranked (see the else branch below).
  const score = !isTerminal ? fitScore : null;

  // Fit band for color token selection (spec: hsl(var(--strong/moderate/weak))).
  // Banded off fit_overall — the SAME source as the fit chip below — so the
  // score-number color and the chip category are guaranteed to agree.
  const band = !isTerminal ? fitOverall : null;

  // Fit-band color token (spec: `const col = hsl(fitHsl(c.band))`).
  // Using CSS token string — applied via style= prop. NOT hsl(var(--chart-N)).
  const fitColorToken =
    band === "strong"
      ? "hsl(var(--strong))"
      : band === "moderate"
        ? "hsl(var(--moderate))"
        : "hsl(var(--weak))";

  // Header tint: spec `const tint = hsl(fv / 0.09)`.
  const headTint =
    band != null
      ? band === "strong"
        ? "hsl(var(--strong) / 0.09)"
        : band === "moderate"
          ? "hsl(var(--moderate) / 0.09)"
          : "hsl(var(--weak) / 0.09)"
      : "color-mix(in srgb, var(--surface-subtle) 30%, transparent)";

  // Border-top color: spec `style="border-top:3px solid ${col}"`.
  const borderTopColor = band != null ? fitColorToken : "var(--text-secondary)";

  // Rank badge: spec `.rank-badge.top` when r <= 5 (brand-tinted bg + brand text);
  // plain secondary otherwise.
  const rankIsTop = displayRank != null && displayRank <= 5;

  // Skills: up to 3, honest-empty guard.
  const skills = (skillsTags ?? []).slice(0, 3);

  // Profile URL: v2 route with optional JD context.
  const profileHref = `${base}/candidates/${candidateId}${
    selectedJdId ? `?jd=${encodeURIComponent(selectedJdId)}` : ""
  }`;

  // Status badge variant (secondary vs outline — matches spec `c.st`).
  const stVariant = statusVariant(status);

  return (
    <article
      data-testid="v2-candidate-card"
      data-rank={displayRank ?? "none"}
      onClick={() => { window.location.href = profileHref; }}
      className={cn(
        // Base card: spec .cand-card
        "relative flex cursor-pointer flex-col",
        "rounded-[var(--radius)] border border-border-default",
        "bg-surface-card shadow-[var(--shadow-sm)]",
        // Hover lift: spec `.cand-card:hover { transform:translateY(-2px); box-shadow:var(--shadow-md); }`
        "transition-[transform,box-shadow,border-color] duration-[160ms]",
        "hover:-translate-y-[2px] hover:shadow-[var(--shadow-md)]",
        // Terminal: spec `.cand-card.terminal { opacity:.6; filter:saturate(.5); }`
        // Hover restores: `.cand-card.terminal:hover { opacity:1; filter:none; }`
        isTerminal && "opacity-[.6] saturate-[.5] hover:opacity-100 hover:saturate-100",
      )}
      style={{ borderTop: `3px solid ${borderTopColor}` }}
    >
      {/* ── cc-head: rank badge + fit score ───────────────────────────────── */}
      {/* spec: .cc-head { flex space-between; align-items:center; padding:12px 16px 10px } */}
      <div
        className="flex items-center justify-between"
        style={{ padding: "12px 16px 10px", background: headTint }}
      >
        {/* Rank badge: spec .rank-badge / .rank-badge.top */}
        {displayRank != null ? (
          <span
            className={cn(
              "inline-grid place-items-center tabular-nums shrink-0",
              // spec: 24×24, radius 6px, 12px/650
            )}
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: 650,
              // .rank-badge.top: hsl(var(--brand)/0.12) bg + brand text
              // .rank-badge: var(--surface-subtle) bg + secondary-fg text
              background: rankIsTop
                ? "hsl(var(--brand,214_100%_41%) / 0.12)"
                : "var(--surface-subtle)",
              color: rankIsTop
                ? "hsl(var(--brand,214_100%_41%))"
                : "var(--text-primary)",
            }}
            aria-label={`Rank ${displayRank}`}
          >
            {displayRank}
          </span>
        ) : (
          // No rank yet: empty placeholder to keep flex layout balanced
          <span className="inline-block" style={{ width: "24px" }} aria-hidden />
        )}

        {/* Fit score: spec .cc-fit { text-align:right }
         *   .n = 22px/700, fit color; .l = 9.5px/700, uppercase, opacity .82
         * Hidden for terminal candidates (spec: c.terminal?'':cc-fit block) */}
        {score != null ? (
          <div className="text-right" data-testid="cc-fit-score">
            <div
              className="tabular-nums leading-none"
              style={{ fontSize: "22px", fontWeight: 700, color: fitColorToken }}
            >
              {score}
            </div>
            <div
              className="uppercase"
              style={{
                fontSize: "9.5px",
                fontWeight: 700,
                letterSpacing: "0.1em",
                opacity: 0.82,
                color: fitColorToken,
              }}
            >
              Fit
            </div>
          </div>
        ) : isTerminal ? (
          // Terminal: show status label instead of score
          <div
            className="text-right text-text-secondary"
            style={{ fontSize: "9.5px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}
          >
            {status === "opted_out" ? "Opted out" : "Rejected"}
          </div>
        ) : (
          // Non-terminal but unranked (NULL fit_score): honest "—", identical to
          // the v2 table's fit column — never a fabricated placeholder number.
          <div
            className="text-right text-text-secondary"
            data-testid="cc-fit-score"
            style={{ fontSize: "22px", fontWeight: 700, lineHeight: 1 }}
          >
            —
          </div>
        )}
      </div>

      {/* ── cc-id: avatar + name + status badge ───────────────────────────── */}
      {/* spec: .cc-id { flex-col; align-items:center; gap:9px; padding:4px 16px 14px } */}
      <div
        className="flex flex-col items-center"
        style={{ gap: "9px", padding: "4px 16px 14px" }}
      >
        {/* Field 3: Avatar — CandidatePhoto (72px) auth-fetches the real
            candidate photo (Unavatar/LinkedIn blob via apiClient) and falls
            back to gradient-initials on 404. Same photo data path the V1
            Dashboard card (CanonCandidateCard) and V1 table use, so v2 shows
            the SAME faces V1 shows. `size="lg"` → Avatar.xl = 72px. */}
        <CandidatePhoto candidateId={candidateId} candidateName={name} size="lg" />

        {/* Field 4: Name — spec: .cc-id .name { 15px; 650; text-align:center; lh 1.2 } */}
        <div
          className="text-center text-text-primary"
          style={{ fontSize: "15px", fontWeight: 650, lineHeight: 1.2 }}
          data-testid="cc-name"
        >
          {name}
        </div>

        {/* Field 5: Status badge — spec: .badge.badge-{c.st} */}
        {/* secondary: var(--surface-subtle) bg + secondary-fg text */}
        {/* outline:   var(--border-default) border + muted-fg text */}
        <span
          className={cn(
            "inline-flex items-center rounded-full",
            "text-[11px] font-medium",
          )}
          style={{
            padding: "2px 10px",
            background:
              stVariant === "secondary"
                ? "var(--surface-subtle)"
                : "var(--surface-canvas)",
            color:
              stVariant === "secondary"
                ? "var(--text-primary)"
                : "var(--text-secondary)",
            border: stVariant === "outline" ? "1px solid var(--border-default)" : "none",
          }}
          data-testid="cc-status-badge"
        >
          {STATUS_LABELS[status] ?? status}
        </span>
      </div>

      {/* ── cc-body: skills → why → chips ─────────────────────────────────── */}
      {/* spec: .cc-body { flex-col; gap:11px; border-top; padding:13px 16px; flex:1 1 auto } */}
      <div
        className="flex flex-1 flex-col border-t border-border-default"
        style={{ gap: "11px", padding: "13px 16px" }}
      >
        {/* Field 6: Key skills chips (honest-empty: hidden when empty) */}
        {skills.length > 0 && (
          <div>
            {/* spec: .cc-body .lbl { 9.5px; 700; uppercase; 0.1em; --muted-fg; mb 5px } */}
            <div
              className="text-text-secondary uppercase"
              style={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: "0.1em", marginBottom: "5px" }}
            >
              Key skills
            </div>
            {/* spec: .cc-skills { flex-wrap; gap:5px }; .sk { rounded-full; 10.5px/550; px8 py2 }
             *
             * VISIBILITY FIX (v2 light theme): the spec's `background: var(--surface-subtle)`
             * renders INVISIBLE on the light theme — `--secondary` (and `--muted`) both alias
             * `--surface-subtle`, a near-white gray, so on a white card the skill chips read as
             * flat text with no pill (cofounder-confirmed on deployed 880b0800). The fit/tenure
             * chips below stay visible only because they use saturated `*-soft` tints.
             *
             * Blue-forward outlined pill — same shape/family as the fit/tenure chips (rounded-full,
             * 10.5px/550, px8 py2), matching the V1 dashboard's outlined skill pills
             * (CanonCandidateCard: blue-tint bg + blue-tint border): `--brand-soft` fill +
             * brand-tinted border + brand text. Token-driven (no raw hex; `--brand-soft`/`--brand`
             * are allowlisted for this file), so it reads as a clear pill on both light and dark. */}
            <div className="flex flex-wrap" style={{ gap: "5px" }} data-testid="cc-skills">
              {skills.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center"
                  style={{
                    borderRadius: "999px",
                    background: "hsl(var(--brand-soft))",
                    color: "hsl(var(--brand,214_100%_41%))",
                    border: "1px solid hsl(var(--brand,214_100%_41%) / 0.22)",
                    padding: "2px 8px",
                    fontSize: "10.5px",
                    fontWeight: 550,
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Field 7: AI why blurb (honest-empty: hidden when null/missing) */}
        {summary && (
          <div>
            {/* spec: .lbl */}
            <div
              className="text-text-secondary uppercase"
              style={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: "0.1em", marginBottom: "5px" }}
            >
              AI analysis — why
            </div>
            {/* spec: .cc-why { 12px; lh 1.5; --muted-fg; -webkit-line-clamp:3 } */}
            <p
              className="text-text-secondary"
              style={{
                fontSize: "12px",
                lineHeight: 1.5,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              data-testid="cc-why"
            >
              {summary}
            </p>
          </div>
        )}

        {/* cc-chips: fit band chip (field 9) + tenure chip (field 8) */}
        {/* spec: .cc-chips { flex-wrap; gap:6px; margin-top:1px } */}
        {(fitOverall != null || tenureSignal != null) && (
          <div className="flex flex-wrap" style={{ gap: "6px", marginTop: "1px" }} data-testid="cc-chips">
            {/* Fit band chip: spec `.badge.badge-${c.band}` with dot */}
            {fitOverall != null && (
              <span
                className="inline-flex items-center gap-[4px]"
                style={{
                  borderRadius: "999px",
                  padding: "2px 8px",
                  fontSize: "10.5px",
                  fontWeight: 600,
                  background:
                    fitOverall === "strong"
                      ? "hsl(var(--strong-soft))"
                      : fitOverall === "moderate"
                        ? "hsl(var(--moderate-soft))"
                        : "hsl(var(--weak-soft))",
                  color:
                    fitOverall === "strong"
                      ? "hsl(var(--strong))"
                      : fitOverall === "moderate"
                        ? "hsl(var(--moderate))"
                        : "hsl(var(--weak))",
                }}
                data-testid="cc-fit-chip"
              >
                <span
                  className="inline-block rounded-full shrink-0"
                  style={{
                    width: "6px",
                    height: "6px",
                    background:
                      fitOverall === "strong"
                        ? "hsl(var(--strong))"
                        : fitOverall === "moderate"
                          ? "hsl(var(--moderate))"
                          : "hsl(var(--weak))",
                  }}
                />
                {fitOverall.charAt(0).toUpperCase() + fitOverall.slice(1)} fit
              </span>
            )}

            {/* Tenure chip: spec `.badge.badge-${tenTone}` */}
            {tenureSignal != null && (
              <span
                className="inline-flex items-center"
                style={{
                  borderRadius: "999px",
                  padding: "2px 8px",
                  fontSize: "10.5px",
                  fontWeight: 600,
                  background:
                    tenureSignal === "strong"
                      ? "hsl(var(--strong-soft))"
                      : tenureSignal === "moderate"
                        ? "hsl(var(--moderate-soft))"
                        : "hsl(var(--weak-soft))", // concern → weak palette
                  color:
                    tenureSignal === "strong"
                      ? "hsl(var(--strong))"
                      : tenureSignal === "moderate"
                        ? "hsl(var(--moderate))"
                        : "hsl(var(--weak))", // concern → weak palette
                }}
                data-testid="cc-tenure-chip"
              >
                {tenureSignal === "concern" ? "Concern" : tenureSignal.charAt(0).toUpperCase() + tenureSignal.slice(1)} tenure
              </span>
            )}
          </div>
        )}

        {/* Empty body state for terminal/unranked with no data */}
        {!summary && skills.length === 0 && fitOverall == null && tenureSignal == null && (
          <p
            className="flex-1 text-text-secondary italic"
            style={{ fontSize: "12px", lineHeight: 1.5 }}
            data-testid="cc-no-data"
          >
            {isTerminal
              ? `${status === "opted_out" ? "Opted out" : "Rejected"} — excluded from the active pipeline.`
              : "No analysis yet — ranking in progress."}
          </p>
        )}
      </div>

      {/* ── cc-foot: inline stage-advance (field 11) + "View profile →" (field 10) ── */}
      {/* spec: .cc-foot { border-top; padding:9px 16px }
       *       .vp { inline-flex; center; gap:5px; 12.5px/600; brand color; p5; radius 6px }
       * When a statusControl is supplied it takes the left flex-1 slot and the
       * view link compacts to the right; otherwise the link stays full-width. */}
      <div
        className="flex items-center border-t border-border-default"
        style={{ gap: "8px", padding: "9px 16px" }}
      >
        {statusControl ? (
          <div className="min-w-0 flex-1" data-testid="cc-status-control">
            {statusControl}
          </div>
        ) : null}
        <Link
          to={profileHref}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center justify-center",
            statusControl ? "shrink-0" : "w-full",
          )}
          style={{
            gap: "5px",
            fontSize: "12.5px",
            fontWeight: 600,
            color: "hsl(var(--brand,214_100%_41%))",
            padding: "5px",
            borderRadius: "6px",
          }}
          data-testid="cc-view-profile"
        >
          {statusControl ? "View" : "View profile"}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
            style={{ width: "14px", height: "14px" }}
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </Link>
      </div>
    </article>
  );
}
